import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// Fix import to use the named export 'cameras' instead of default export
import { cameras } from './data/cameras.ts';

// Mapbox token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGFiMSIsImEiOiJjbDZmeGw3bm0wYTN1M2JtamV6cW1jZWVqIn0.e-0qo3T9hooEEc2rnoo9Ag';

function Coverage() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);
  const cameraFeaturesRef = useRef([]);
  const hoverPopupRef = useRef(null);

  // Move these OUTSIDE useEffect:
  function showHoverAtFeature(feature) {
    const mapInstance = map.current;
    const hoverPopup = hoverPopupRef.current;
    if (!mapInstance || !hoverPopup) return;
    const coordinates = feature.geometry.coordinates.slice();
    const props = feature.properties;

    // Function to extract YouTube video ID from various URL formats
    const getYouTubeVideoId = (url) => {
      if (!url || url === "null") return null;
      if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
      }
      const match = url.match(/[?&]v=([^&]+)/);
      return match ? match[1] : null;
    };

    const videoId = getYouTubeVideoId(props.video_url);

    // Build popup content with video if available
    const content = `
      <div style="padding: 5px 10px;">
        <div style="font-size: 16px; font-weight: 600; margin-bottom: 12px;">${props.location}</div>
        ${videoId ? `
          <div style="width: 400px; height: 225px; margin-bottom: 8px;">
            <iframe 
              width="100%" 
              height="100%" 
              src="https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen
              style="border-radius: 8px;"
            ></iframe>
          </div>
        ` : ''}
      </div>
    `;

    hoverPopup.setLngLat(coordinates).setHTML(content).addTo(mapInstance);
  }

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize Mapbox focused on San Francisco
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-122.4194, 37.7749], // San Francisco coordinates
      zoom: 1, // Zoomed out to show the globe
      projection: 'globe'
    });

    // Add navigation controls (zoom buttons)
    mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

    map.current = mapInstance;

    // Wait for map to load before adding markers
    mapInstance.on('load', () => {
      mapInstance.setFog({
        'color': 'rgba(186, 210, 235, 0.2)',
        'high-color': 'rgba(36, 92, 223, 0.2)',
        'horizon-blend': 0.02,
        'space-color': '#000000',
        'star-intensity': 0.6
      });

      // Add 3D buildings layer
      // The 'building' layer in the Mapbox Streets style contains building-height data
      // We need to add an extrusion layer based on that data
      mapInstance.addLayer({
        'id': '3d-buildings',
        'source': 'composite',
        'source-layer': 'building',
        'type': 'fill-extrusion',
        'minzoom': 15, // Only show buildings when zoomed in
        'paint': {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': [
            'interpolate', ['linear'], ['zoom'],
            15, 0,
            16, ['get', 'height']
          ],
          'fill-extrusion-base': ['get', 'min_height'],
          'fill-extrusion-opacity': 0.6
        }
      });

      // Group cameras by their coordinates to identify duplicates
      const coordinateGroups = {};
      cameras.forEach(camera => {
        const coordKey = `${camera.lon}-${camera.lat}`;
        if (!coordinateGroups[coordKey]) {
          coordinateGroups[coordKey] = [];
        }
        coordinateGroups[coordKey].push(camera);
      });

      // Convert camera data with jittering for duplicates
      const cameraFeatures = [];
      Object.values(coordinateGroups).forEach(cameraGroup => {
        // If only one camera at this location, no jittering needed
        if (cameraGroup.length === 1) {
          cameraFeatures.push({
            type: 'Feature',
            properties: {
              name: cameraGroup[0].location || 'Camera',
              ...cameraGroup[0],
              neighborhood: cameraGroup[0].neighborhood || 'Other'
            },
            geometry: {
              type: 'Point',
              coordinates: [Number(cameraGroup[0].lon), Number(cameraGroup[0].lat)]
            }
          });
        } else {
          // Multiple cameras at same location, apply jittering
          cameraGroup.forEach((camera, index) => {
            // Create a small offset based on index (in a circular pattern)
            const angle = (index / cameraGroup.length) * Math.PI * 2;
            // Adjust the magnitude of jittering based on zoom level - using a small value
            const jitterAmount = 0.0002; // About 20 meters

            const jitteredLon = Number(camera.lon) + Math.cos(angle) * jitterAmount;
            const jitteredLat = Number(camera.lat) + Math.sin(angle) * jitterAmount;

            cameraFeatures.push({
              type: 'Feature',
              properties: {
                name: camera.location || 'Camera',
                originalLon: camera.lon,
                originalLat: camera.lat,
                camera_count: cameraGroup.length,
                ...camera,
                neighborhood: camera.neighborhood || 'Other'
              },
              geometry: {
                type: 'Point',
                coordinates: [jitteredLon, jitteredLat]
              }
            });
          });
        }
      });

      // Add camera points from the imported data with clustering
      mapInstance.addSource('camera-points', {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': cameraFeatures
        },
        'cluster': true,
        'clusterMaxZoom': 14, // Max zoom to cluster points
        'clusterRadius': 50 // Radius of each cluster when clustering points
      });

      // Create custom camera icon directly without loading external resources
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 64;
      canvas.width = size;
      canvas.height = size;

      // Draw futuristic camera icon - simpler design
      ctx.fillStyle = 'rgba(0, 0, 0, 0)';
      ctx.fillRect(0, 0, size, size);

      // Outer ring glow effect
      const gradient = ctx.createRadialGradient(size / 2, size / 2, size / 4, size / 2, size / 2, size / 2);
      gradient.addColorStop(0, 'rgba(74, 144, 226, 0.8)');
      gradient.addColorStop(0.5, 'rgba(74, 144, 226, 0.3)');
      gradient.addColorStop(1, 'rgba(74, 144, 226, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();

      // Inner camera body
      ctx.fillStyle = 'rgba(20, 20, 30, 0.9)';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 4, 0, Math.PI * 2);
      ctx.fill();

      // Camera lens
      ctx.fillStyle = 'rgba(120, 160, 255, 0.7)';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 8, 0, Math.PI * 2);
      ctx.fill();

      // Add a pulse effect
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 3, 0, Math.PI * 2);
      ctx.stroke();

      // Add the image to the map
      mapInstance.addImage('camera-icon', { width: size, height: size, data: ctx.getImageData(0, 0, size, size).data });

      // Create another image for clustered points
      const clusterCanvas = document.createElement('canvas');
      const clusterCtx = clusterCanvas.getContext('2d');
      clusterCanvas.width = size;
      clusterCanvas.height = size;

      // Draw cluster icon background
      const clusterGradient = clusterCtx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      clusterGradient.addColorStop(0, 'rgba(74, 144, 226, 0.8)');
      clusterGradient.addColorStop(1, 'rgba(74, 144, 226, 0.4)');

      clusterCtx.fillStyle = clusterGradient;
      clusterCtx.beginPath();
      clusterCtx.arc(size / 2, size / 2, size / 3, 0, Math.PI * 2);
      clusterCtx.fill();

      // Add outer ring
      clusterCtx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      clusterCtx.lineWidth = 2;
      clusterCtx.beginPath();
      clusterCtx.arc(size / 2, size / 2, size / 3, 0, Math.PI * 2);
      clusterCtx.stroke();

      mapInstance.addImage('cluster-icon', { width: size, height: size, data: clusterCtx.getImageData(0, 0, size, size).data });

      // Now add the layers with our custom icons
      // Replace the unclustered points layer with symbol layer using our custom icon
      mapInstance.addLayer({
        'id': 'unclustered-point',
        'type': 'symbol',
        'source': 'camera-points',
        'filter': ['!', ['has', 'point_count']],
        'layout': {
          'icon-image': 'camera-icon',
          'icon-size': 0.5,
          'icon-allow-overlap': true
        }
      });

      // Replace clusters with custom icon
      mapInstance.addLayer({
        'id': 'clusters',
        'type': 'symbol',
        'source': 'camera-points',
        'filter': ['has', 'point_count'],
        'layout': {
          'icon-image': 'cluster-icon',
          'icon-size': [
            'interpolate', ['linear'], ['get', 'point_count'],
            1, 0.7,
            10, 0.9,
            25, 1.1
          ],
          'icon-allow-overlap': true
        }
      });

      // Keep the cluster count layer on top
      mapInstance.addLayer({
        'id': 'cluster-count',
        'type': 'symbol',
        'source': 'camera-points',
        'filter': ['has', 'point_count'],
        'layout': {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 14
        },
        'paint': {
          'text-color': '#ffffff'
        }
      });

      // Add click handler to expand clusters
      mapInstance.on('click', 'clusters', (e) => {
        const features = mapInstance.queryRenderedFeatures(e.point, {
          layers: ['clusters']
        });
        const clusterId = features[0].properties.cluster_id;
        mapInstance.getSource('camera-points').getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;

            mapInstance.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom
            });
          }
        );
      });

      // Update mouse cursor and popups for both clusters and unclustered points
      mapInstance.on('mouseenter', 'clusters', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'clusters', () => {
        mapInstance.getCanvas().style.cursor = '';
      });

      // Then, update the popup initialization and event handlers
      const popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        maxWidth: '300px',
        className: 'orchestra-popup',
        anchor: 'bottom',
        offset: [0, -10]
      });

      mapInstance.on('click', 'unclustered-point', (e) => {
        // Get coordinates and properties
        const coordinates = e.features[0].geometry.coordinates.slice();
        const props = e.features[0].properties;

        // Create a unique ID for the container
        const contentId = `content-${props.camera_id || Date.now()}`;

        // Function to extract YouTube video ID from various URL formats
        const getYouTubeVideoId = (url) => {
          if (!url || url === "null") return null;

          // Handle youtu.be format
          if (url.includes('youtu.be/')) {
            return url.split('youtu.be/')[1].split('?')[0];
          }

          // Handle youtube.com/watch format
          const match = url.match(/[?&]v=([^&]+)/);
          return match ? match[1] : null;
        };

        const videoId = getYouTubeVideoId(props.video_url);

        // Create popup content - only show video section if we have a video
        const content = `
          <div class="orchestra-popup-header">
            <div style="font-weight: 500; color: rgba(255, 255, 255, 0.9); font-size: 14px;">${props.location || 'Camera'}</div>
          </div>
          ${videoId ?
            `<div id="${contentId}" style="width: 100%; height: 150px; margin-bottom: 8px; background: rgba(0,0,0,0.2);">
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
                style="border-radius: 4px;"
              ></iframe>
            </div>` :
            `<div id="${contentId}" style="display:none;"></div>`
          }
          <div class="orchestra-popup-body">
            ${props.camera_count ? `
              <div class="orchestra-popup-data-row">
                <div class="orchestra-popup-label">Camera Group</div>
                <div class="orchestra-popup-value">${props.camera_count} cameras</div>
              </div>
            ` : ''}
            <div class="orchestra-popup-data-row">
              <div class="orchestra-popup-label">Status</div>
              <div class="orchestra-popup-value" style="color: #4DC382;">Active</div>
            </div>
            <div style="margin-top: 8px; text-align: center;">
              <a href="https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coordinates[1]},${coordinates[0]}" target="_blank" class="street-view-link" style="font-size: 12px; color: rgba(74, 144, 226, 0.9); text-decoration: none; border-bottom: 1px dotted rgba(74, 144, 226, 0.5);">Look at Street View</a>
            </div>
          </div>
        `;

        // Set popup content and location
        popup.setLngLat(coordinates).setHTML(content).addTo(mapInstance);
      });

      // For mouseover/hover, show a simpler tooltip-style popup
      const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'orchestra-popup orchestra-hover-popup',
        anchor: 'bottom',
        offset: [0, -5],
        maxWidth: '500px'
      });

      mapInstance.on('mouseenter', 'unclustered-point', (e) => {
        // Change cursor style
        mapInstance.getCanvas().style.cursor = 'pointer';

        // Get coordinates and properties
        const coordinates = e.features[0].geometry.coordinates.slice();
        const location = e.features[0].properties.location;

        // Only show hover popup if the click popup isn't visible
        if (!popup.isOpen()) {
          // Simplified hover popup
          const content = `
            <div style="padding: 5px 10px;">
              <div style="font-size: 12px; font-weight: 500;">${location}</div>
            </div>
          `;

          // Set popup content and location
          hoverPopup.setLngLat(coordinates).setHTML(content).addTo(mapInstance);
        }
      });

      mapInstance.on('mouseleave', 'unclustered-point', () => {
        mapInstance.getCanvas().style.cursor = '';
        hoverPopup.remove();
      });

      // Helper function to format time ago
      function getTimeAgo(timestamp) {
        const now = new Date();
        const past = new Date(timestamp);
        const diffMs = now - past;

        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) return `${diffDays}d ago`;
        if (diffHours > 0) return `${diffHours}h ago`;
        if (diffMins > 0) return `${diffMins}m ago`;
        return `${diffSecs}s ago`;
      }

      // Create a single style element that includes both the pulsing animation and popup styles
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        /* Pulsing animation for camera icons */
        @keyframes cameraPulse {
          0% {
            opacity: 0.9;
            transform: scale(0.98);
          }
          50% {
            opacity: 1;
            transform: scale(1.02);
          }
          100% {
            opacity: 0.9;
            transform: scale(0.98);
          }
        }
        
        .mapboxgl-canvas-container .mapboxgl-marker {
          animation: cameraPulse 3s infinite ease-in-out;
        }
        
        /* Popup styling */
        .orchestra-popup {
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }
        
        .orchestra-popup .mapboxgl-popup-content {
          background: rgba(15, 17, 26, 0.85);
          backdrop-filter: blur(8px);
          border-radius: 4px;
          border: 1px solid rgba(74, 144, 226, 0.3);
          padding: 0;
          overflow: hidden;
        }
        
        .orchestra-popup .mapboxgl-popup-tip {
          border-top-color: rgba(74, 144, 226, 0.5);
          border-bottom-color: rgba(74, 144, 226, 0.5);
        }
        
        .orchestra-popup-header {
          padding: 8px 12px;
          border-bottom: 1px solid rgba(74, 144, 226, 0.2);
        }
        
        .orchestra-popup-body {
          padding: 10px 12px;
        }
        
        .orchestra-popup-data-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        
        .orchestra-popup-label {
          opacity: 0.7;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .orchestra-popup-value {
          font-size: 12px;
          text-align: right;
        }
        
        .orchestra-popup .mapboxgl-popup-close-button {
          color: rgba(255, 255, 255, 0.5);
          font-size: 18px;
          padding: 4px 8px;
          right: 0;
          top: 0;
        }
        
        .orchestra-popup .mapboxgl-popup-close-button:hover {
          color: rgba(255, 255, 255, 0.8);
          background: none;
        }
      `;
      document.head.appendChild(styleElement);

      // Setup pulsing animation for camera icons
      let pulseStep = 0;
      const PULSE_STEPS = 30;
      const animatePulse = () => {
        pulseStep = (pulseStep + 1) % PULSE_STEPS;
        const pulseFactor = Math.sin((pulseStep / PULSE_STEPS) * Math.PI);

        // Update with a smaller factor (0.05 instead of 0.15)
        mapInstance.setLayoutProperty('unclustered-point', 'icon-size', 0.48 + pulseFactor * 0.05);

        // Request the next frame
        requestAnimationFrame(animatePulse);
      };

      // Start the pulse animation
      animatePulse();

      // Make sure to clean up the animation on unmount in the return function
      const animationFrame = requestAnimationFrame(animatePulse);

      // After initial flyTo animation completes:
      const cameraCoords = cameraFeatures.map(f => f.geometry.coordinates);

      // Filter out cameras with no video
      const featuresWithVideo = cameraFeatures.filter(
        f => f.properties.video_url && f.properties.video_url !== "null"
      );

      // Sort by camera_id (ascending, as number)
      featuresWithVideo.sort((a, b) => Number(a.properties.camera_id) - Number(b.properties.camera_id));

      // Save to ref for flyover
      cameraFeaturesRef.current = featuresWithVideo;
      hoverPopupRef.current = hoverPopup;

      return () => {
        cancelAnimationFrame(animationFrame);
        mapInstance.remove();
      };
    });
  }, [googleMapsLoaded]);

  // Move the Google Maps API loading useEffect outside of the other useEffect
  useEffect(() => {
    // Load Google Maps API if it's not already loaded
    if (!window.google || !window.google.maps) {
      const googleMapsScript = document.createElement('script');
      googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=YOUR_GOOGLE_MAPS_API_KEY&libraries=streetview`;
      googleMapsScript.async = true;
      googleMapsScript.defer = true;
      document.head.appendChild(googleMapsScript);

      googleMapsScript.onload = () => {
        setGoogleMapsLoaded(true);
      };
    } else {
      setGoogleMapsLoaded(true);
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Function to start camera animation sequence
  const startCameraAnimation = () => {
    if (!map.current) return;

    setShowHeader(false);

    // Zoom in to San Francisco with a slight tilt
    map.current.flyTo({
      center: [-122.4194, 37.7749],
      zoom: 14,
      pitch: 45,
      bearing: 15,
      duration: 3000,
      essential: true
    });

    // Just set animation complete after zoom finishes
    setTimeout(() => {
      setAnimationComplete(true);
    }, 3500);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Map container */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100vh' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      </div>

      {/* Header - only shown if showHeader is true */}
      {showHeader && (
        <div className="relative z-10 pt-40 px-4 max-w-5xl mx-auto text-center">
          <div className="mb-12 bg-black/70 p-10 rounded-xl backdrop-blur-md border border-white/10 shadow-xl">
            <div className="text-base font-light tracking-[0.3em] mb-3">
              <span className="bg-gradient-to-r from-blue-400 via-white to-blue-400 text-transparent bg-clip-text 
                inline-block">
                ORCHESTRA ENSEMBLE NETWORK
              </span>
            </div>
            <h1 className="text-6xl font-extralight tracking-wider mb-6 bg-gradient-to-r from-white via-white/95 to-white text-transparent bg-clip-text pb-2">
              Coverage
            </h1>
            <p className="text-xl tracking-wide font-light max-w-2xl mx-auto mb-8 text-blue-50/90 leading-relaxed">

            </p>

            {/* Stats section with improved spacing */}
            <div className="flex justify-center items-center mt-10 mb-6">
              <div className="bg-black/40 px-10 py-4 rounded-full border border-blue-300/20 backdrop-blur-sm w-auto">
                <div className="text-center px-6">
                  <span className="block text-3xl font-light text-blue-300">50</span>
                  <span className="text-sm uppercase tracking-wider text-blue-100/70">Live Cameras in SF</span>
                </div>
              </div>
            </div>

            {/* Explore button */}
            <button
              onClick={startCameraAnimation}
              className="mt-4 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full text-white font-medium tracking-wide transform transition hover:scale-105 hover:shadow-lg focus:outline-none"
            >
              Explore Coverage
            </button>
          </div>
        </div>
      )}

      {/* Instructions that appear after animation completes */}
      {!showHeader && animationComplete && (
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-black/60 px-6 py-3 rounded-full backdrop-blur-sm text-white/90 text-sm font-light tracking-wide animate-fadeIn">
          Click on any camera to see footage and explore
        </div>
      )}
    </div>
  );
}

export default Coverage;